import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResumeTailoring } from '@/contexts/ResumeTailoringContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, ArrowLeft, Briefcase, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { StepHeader } from './StepHeader';

export function JobInformationPage() {
  const navigate = useNavigate();
  const { state, dispatch } = useResumeTailoring();
  const { toast } = useToast();

  const [jobTitle, setJobTitle] = useState(state.jobPositionTitle || '');
  const [company, setCompany] = useState('');
  const [jobDescription, setJobDescription] = useState(state.jobDescription || '');

  const canContinue = jobTitle.trim().length > 0 && jobDescription.trim().length > 50;

  const handleContinue = () => {
    if (!jobTitle.trim()) {
      toast({ title: 'Job title is required', variant: 'destructive' });
      return;
    }
    if (jobDescription.trim().length < 50) {
      toast({ title: 'Please paste a full job description', description: 'At least 50 characters required.', variant: 'destructive' });
      return;
    }
    dispatch({
      type: 'SET_JOB_DATA',
      payload: {
        tailoringSource: 'job-description',
        jobDescription,
        jobPositionTitle: jobTitle,
        jobPositionIndustry: company,
        selectedJobId: '',
      },
    });
    navigate('/resume-tailoring-simple/step/3');
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8">
      <StepHeader currentStep={2} />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            Job Details
          </CardTitle>
          <CardDescription>Tell us about the role you're targeting</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="job-title">Job Title <span className="text-destructive">*</span></Label>
              <Input
                id="job-title"
                placeholder="e.g. Senior Marketing Manager"
                value={jobTitle}
                onChange={e => setJobTitle(e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company">Company <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                id="company"
                placeholder="e.g. Google, Amazon"
                value={company}
                onChange={e => setCompany(e.target.value)}
                className="h-11"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="job-description">
              Job Description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="job-description"
              placeholder="Paste the full job description here — responsibilities, requirements, preferred skills, everything. The more detail, the better the tailoring."
              value={jobDescription}
              onChange={e => setJobDescription(e.target.value)}
              rows={14}
              className="resize-none font-mono text-sm"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Include all responsibilities, requirements, and preferred skills</span>
              <span>{jobDescription.length.toLocaleString()} chars</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {canContinue && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-green-50 border border-green-200 mb-6">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800 font-medium">Job info ready — click Continue</p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/resume-tailoring-simple/step/1')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button onClick={handleContinue} disabled={!canContinue} size="lg">
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
